import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CreateSkyviewExportJobRequestBody,
  OrthophotoCompression,
  OrthophotoExportFormat,
  PointCloudExportFormat,
} from '../../../../../../../typings/api/skymap/rest/v1/project';
import { SkyMapAxiosServiceFactory } from '../../../../../services/axios/skymap-axios-service-factory';
import { isValidNumber } from '../../../../../utils/math/number';
import { gsdToMeters, GsdUnit } from '../../../../../utils/proj4/units';
import { isDefined } from '../../../../../utils/variables';
import { InputHandler } from '../../../../../viewer/input-handler';
import { publish, SubscriptionTopic } from '../../../../../viewer/messaging/pubsub';
import { ProjectGeodataManager } from '../../../../../viewer/project-geodata-manager';
import { useCoordinateSystemsAndGeoidsState } from '../../../../hooks/use-coordinate-systems-and-geoids-state';
import { useErrorHandling } from '../../../../hooks/use-error-handling';
import { useGsdUnit } from '../../../../hooks/use-gsd-unit';
import {
  getTimelineOrthotileInfo,
  useProjectOrthotiles,
} from '../../../../hooks/use-project-orthotiles';
import {
  getTimelinePointCloudInfo,
  useProjectPointClouds,
} from '../../../../hooks/use-project-point-clouds';
import { useProjectSnapshot } from '../../../../hooks/use-project-snapshot';
import {
  getTimelineTiledModelInfo,
  useProjectTiledModels,
} from '../../../../hooks/use-project-tiled-models';
import { useUserState } from '../../../../state/user-state/user-state';
import { Button } from '../../../button/button';
import { CheckBox } from '../../../check-box/check-box';
import { DropdownSuggestion } from '../../../dropdown-suggestion/dropdown-suggesion';
import { InfoBox } from '../../../info-box/info-box';
import { LabelledContainer } from '../../../labelled-container/labelled-container';
import { RadioButtons } from '../../../radio-buttons/radio-buttons';
import { Select } from '../../../select/select';
import { CollapsableSection } from '../../../skyview/right-menu/components/collapsable-section';
import { CollapsableSectionGroup } from '../../../skyview/right-menu/components/collapsable-section-group';
import { RightPanelContent, RightPanelFooter } from '../../../skyview/right-menu/components/styles';
import { Stack } from '../../../stack/stack';
import { TextBox } from '../../../text-box/text-box';

type ExportType = 'orthophoto' | 'pointcloud' | 'tiledData' | 'cesium3DTiles';

type CreateSkyviewExportJobParams = {
  projectId: string;
  body: CreateSkyviewExportJobRequestBody;
};

const useCreateSkyviewExportJobMutation = () => {
  const { t } = useTranslation();
  const { clearErrors: clearApiErrors, handleError, buildErrorList } = useErrorHandling();
  const errorList = useMemo(() => buildErrorList(), [buildErrorList]);

  const mutation = useMutation({
    mutationFn: (params: CreateSkyviewExportJobParams) => {
      return SkyMapAxiosServiceFactory.instance.createProjectServiceV1().createSkyviewExportJob({
        path: {
          projectId: params.projectId,
        },
        body: params.body,
      });
    },
    retry: false,
    networkMode: 'always',
    onSuccess: () => {
      publish(SubscriptionTopic.ToastrMessage, {
        type: 'info',
        message: t('onDemand.skyviewExport.success', { ns: 'components' }),
      });
      InputHandler.instance.setTool('select');
    },
    onMutate: () => clearApiErrors(),
    onError: (err) => {
      handleError(err);
      publish(SubscriptionTopic.ToastrMessage, {
        type: 'error',
        message: t('onDemand.skyviewExport.error', { ns: 'components' }),
      });
    },
  });

  return {
    createExportJob: mutation.mutate,
    isPending: mutation.isPending,
    errorList,
  };
};

/** 2 in ≈ 0.05 m. */
const defaultRasterResolution: Record<GsdUnit, string> = { m: '0.05', in: '2' };

const Component = () => {
  const { projectId } = useProjectSnapshot();
  const orthoTilesData = useProjectOrthotiles();
  const pointCloudsData = useProjectPointClouds();
  const tiledModelsData = useProjectTiledModels();
  const { t } = useTranslation();
  const gsdUnit = useGsdUnit();
  const [exportType, setExportType] = useState<ExportType>('orthophoto');
  const [selectedOrthotileIndex, setSelectedOrthotileIndex] = useState<string>();
  const [selectedPointCloudIndex, setSelectedPointCloudIndex] = useState<string>();
  const [selectedTiledModelIndex, setSelectedTiledModelIndex] = useState<string>();
  const payloadRef = useRef<CreateSkyviewExportJobRequestBody>({});

  // Orthotile options
  const [orthoFileFormat, setOrthoFileFormat] = useState<OrthophotoExportFormat>('GTiff');
  const [orthoCompression, setOrthoCompression] = useState<OrthophotoCompression>('LZW');
  const [orthoMaxGsd, setOrthoMaxGsd] = useState<string>('');
  const [orthoCreateWorldFile, setOrthoCreateWorldFile] = useState<boolean>(false);

  // Point cloud options
  const [pointCloudFormat, setPointCloudFormat] = useState<PointCloudExportFormat>('LAZ');
  const [pointCloudRasterResolution, setPointCloudRasterResolution] = useState<string>(
    defaultRasterResolution[gsdUnit],
  );

  // Tiling options
  const [tilingPixelSize, setTilingPixelSize] = useState<string>('5000');
  const [tilingFallbackBlockSizeMeters, setTilingFallbackBlockSizeMeters] = useState<string>('50');

  // Cesium3DTiles options
  const [cesium3DTilesFormat, setCesium3DTilesFormat] = useState<'3DTILES' | 'SLPK'>('3DTILES');
  const [convertToWgs84, setConvertToWgs84] = useState<boolean>(true);

  // Coordinate system and geoid state
  const {
    selectedCoordinateSystem,
    setSelectedCoordinateSystem,
    selectedGeoid,
    setSelectedGeoid,
    coordinateSystems,
    geoids,
    isLoading: isLoadingCoordinateSystems,
    getGeoidIdFromItem,
  } = useCoordinateSystemsAndGeoidsState();
  const [transformCoordinateSystem, setTransformCoordinateSystem] = useState<boolean>(false);

  // Collapsable section states
  const [generalSectionExpanded, setGeneralSectionExpanded] = useState<boolean>(true);
  const [orthotileSectionExpanded, setOrthotileSectionExpanded] = useState<boolean>(true);
  const [pointCloudSectionExpanded, setPointCloudSectionExpanded] = useState<boolean>(true);
  const [tilingSectionExpanded, setTilingSectionExpanded] = useState<boolean>(true);

  const placeHolderText = useMemo(
    () =>
      orthoTilesData && pointCloudsData && tiledModelsData
        ? t('search', { ns: 'common' })
        : t('loading', { ns: 'common' }),
    [t, orthoTilesData, pointCloudsData, tiledModelsData],
  );

  const orthoTiles = useMemo(() => {
    if (!isDefined(orthoTilesData)) {
      return [];
    }

    return orthoTilesData
      ?.map((x) => getTimelineOrthotileInfo(x, t))
      .filter(isDefined)
      .sort((a, b) => a.index - b.index);
  }, [orthoTilesData, t]);

  const pointClouds = useMemo(() => {
    if (!isDefined(pointCloudsData)) {
      return [];
    }

    return pointCloudsData
      ?.map((x) => getTimelinePointCloudInfo(x, t))
      .filter(isDefined)
      .sort((a, b) => a.index - b.index);
  }, [pointCloudsData, t]);

  const tiledModels = useMemo(() => {
    if (!isDefined(tiledModelsData)) {
      return [];
    }

    return tiledModelsData
      ?.map((x) => getTimelineTiledModelInfo(x, t))
      .filter(isDefined)
      .sort((a, b) => a.index - b.index);
  }, [tiledModelsData, t]);

  const handleOrthotileSelected = (item: (typeof orthoTiles)[0]) => {
    setSelectedOrthotileIndex(item.id);
  };

  const handlePointCloudSelected = (item: (typeof pointClouds)[0]) => {
    setSelectedPointCloudIndex(item.id);
  };

  const handleTiledModelSelected = (item: (typeof tiledModels)[0]) => {
    setSelectedTiledModelIndex(item.id);
  };

  const showOrthophotoOptions = exportType === 'orthophoto' || exportType === 'tiledData';
  const showPointCloudOptions = exportType === 'pointcloud' || exportType === 'tiledData';
  const showTilingOptions = exportType === 'tiledData';
  const showCesium3DTilesOptions = exportType === 'cesium3DTiles';

  const isExportDisabled = useMemo(() => {
    if (isLoadingCoordinateSystems) {
      return true;
    }

    if (exportType === 'orthophoto') {
      return !isDefined(selectedOrthotileIndex);
    }
    if (exportType === 'pointcloud') {
      return !isDefined(selectedPointCloudIndex);
    }
    if (exportType === 'tiledData') {
      return !isDefined(selectedOrthotileIndex) && !isDefined(selectedPointCloudIndex);
    }
    if (exportType === 'cesium3DTiles') {
      return !isDefined(selectedTiledModelIndex);
    }
    return true;
  }, [
    exportType,
    selectedOrthotileIndex,
    selectedPointCloudIndex,
    selectedTiledModelIndex,
    isLoadingCoordinateSystems,
  ]);

  // Update payload ref when selections change
  useEffect(() => {
    const coordinateSystemId = selectedCoordinateSystem?.id;
    const geoidId = getGeoidIdFromItem(selectedGeoid);

    const payload: CreateSkyviewExportJobRequestBody = {
      coordinateSystemId,
      geoidId,
    };

    // Set orthotile options if export type is orthophoto or tiledData
    if (
      (exportType === 'orthophoto' || exportType === 'tiledData') &&
      isDefined(selectedOrthotileIndex)
    ) {
      const containerIndex = parseInt(selectedOrthotileIndex, 10);
      const timelineContainer = ProjectGeodataManager.instance()
        .getTimelineContainers()
        .find((x) => x.containerIndex === containerIndex);

      if (timelineContainer && timelineContainer.orthotile) {
        payload.orthotileOptions = {
          orthotileId: timelineContainer.orthotile,
          fileFormat: orthoFileFormat,
          compression: orthoCompression,
          maxGsd:
            orthoMaxGsd && isValidNumber(orthoMaxGsd)
              ? gsdToMeters(parseFloat(orthoMaxGsd), gsdUnit)
              : undefined,
          createWorldFile: orthoCreateWorldFile,
        };
      }
    }

    // Set point cloud options if export type is pointcloud or tiledData
    if (
      (exportType === 'pointcloud' || exportType === 'tiledData') &&
      isDefined(selectedPointCloudIndex)
    ) {
      const selectedPointCloud = pointClouds.find((x) => x.id === selectedPointCloudIndex);

      if (selectedPointCloud && 'pointCloudId' in selectedPointCloud) {
        payload.pointCloudOptions = {
          pointCloudId: selectedPointCloud.pointCloudId,
          format: pointCloudFormat,
          rasterResolution:
            pointCloudRasterResolution && isValidNumber(pointCloudRasterResolution)
              ? gsdToMeters(parseFloat(pointCloudRasterResolution), gsdUnit)
              : undefined,
        };
      }
    }

    // Set tiling options
    if (exportType === 'tiledData') {
      payload.tilingOptions = {
        pixelSize:
          tilingPixelSize && isValidNumber(tilingPixelSize)
            ? parseFloat(tilingPixelSize)
            : undefined,
        fallbackBlockSizeMeters:
          tilingFallbackBlockSizeMeters && isValidNumber(tilingFallbackBlockSizeMeters)
            ? parseFloat(tilingFallbackBlockSizeMeters)
            : undefined,
      };
    }

    // Set cesium3DTiles options
    if (exportType === 'cesium3DTiles' && isDefined(selectedTiledModelIndex)) {
      const selectedTiledModel = tiledModels.find((x) => x.id === selectedTiledModelIndex);

      if (selectedTiledModel && 'tiledModelId' in selectedTiledModel) {
        payload.cesium3DTilesOptions = {
          id: selectedTiledModel.tiledModelId,
          format: cesium3DTilesFormat,
          convertToWgs84: convertToWgs84 || undefined,
        };
      }
    }

    payloadRef.current = payload;
  }, [
    exportType,
    selectedOrthotileIndex,
    selectedPointCloudIndex,
    pointClouds,
    orthoFileFormat,
    orthoCompression,
    orthoMaxGsd,
    orthoCreateWorldFile,
    pointCloudFormat,
    pointCloudRasterResolution,
    tilingPixelSize,
    tilingFallbackBlockSizeMeters,
    selectedTiledModelIndex,
    tiledModels,
    cesium3DTilesFormat,
    convertToWgs84,
    selectedCoordinateSystem,
    selectedGeoid,
    getGeoidIdFromItem,
    gsdUnit,
  ]);

  const {
    createExportJob,
    isPending: isExportPending,
    errorList,
  } = useCreateSkyviewExportJobMutation();

  const handleExport = useCallback(() => {
    if (!projectId) {
      return;
    }

    createExportJob({
      projectId,
      body: payloadRef.current,
    });
  }, [projectId, createExportJob]);

  const isOrganizationAdmin = useUserState().isOrganizationAdmin ?? false;
  const pointCloudFormatOptions = useMemo(() => {
    if (isOrganizationAdmin) {
      return [
        { id: 'LAZ', name: 'LAZ' },
        { id: 'LAS', name: 'LAS' },
        { id: 'XYZ', name: 'XYZ' },
        { id: 'E57', name: 'E57' },
        { id: 'DTM', name: 'DTM' },
        { id: 'DSM', name: 'DSM' },
      ];
    }

    return [
      { id: 'LAZ', name: 'LAZ' },
      { id: 'LAS', name: 'LAS' },
      { id: 'XYZ', name: 'XYZ' },
      { id: 'E57', name: 'E57' },
    ];
  }, [isOrganizationAdmin]);

  return (
    <>
      <RightPanelContent useTopPadding={false}>
        <CollapsableSectionGroup>
          <CollapsableSection
            expanded={generalSectionExpanded}
            title={t('onDemand.skyviewExport.title', { ns: 'components' })}
            onSetExpanded={setGeneralSectionExpanded}
          >
            <Stack direction="column" spacing={1}>
              <RadioButtons
                items={[
                  {
                    value: 'orthophoto',
                    name: t('geodata.artifacts.orthoPhoto', { ns: 'common' }),
                  },
                  {
                    value: 'pointcloud',
                    name: t('geodata.artifacts.pointCloud', { ns: 'common' }),
                  },
                  {
                    value: 'cesium3DTiles',
                    name: t('geodata.artifacts.3dModel', { ns: 'common' }),
                  },
                  {
                    value: 'tiledData',
                    name: t('onDemand.skyviewExport.tiles', { ns: 'components' }),
                  },
                ]}
                onChange={(value) => setExportType(value as ExportType)}
              />
              <CheckBox
                checked={transformCoordinateSystem}
                onChange={(e) => setTransformCoordinateSystem(e.target.checked)}
              >
                {t('onDemand.skyviewExport.transformCoordinateSystem', { ns: 'components' })}
              </CheckBox>
              {transformCoordinateSystem &&
              exportType === 'cesium3DTiles' &&
              convertToWgs84 === true ? (
                <InfoBox color="blue">
                  {t('onDemand.skyviewExport.convertToWgs84Info', { ns: 'components' })}
                </InfoBox>
              ) : (
                transformCoordinateSystem && (
                  <>
                    <LabelledContainer
                      text={t('projectSettings.projection.title', { ns: 'components' })}
                    >
                      <DropdownSuggestion
                        disabled={
                          isLoadingCoordinateSystems || !isDefined(selectedCoordinateSystem)
                        }
                        placeholder={
                          isLoadingCoordinateSystems
                            ? t('loading', { ns: 'common' })
                            : selectedCoordinateSystem?.name
                        }
                        portalToBody={true}
                        suggestions={coordinateSystems}
                        value={selectedCoordinateSystem?.id}
                        width="100%"
                        onSuggestionSelected={(suggestion) =>
                          setSelectedCoordinateSystem(suggestion)
                        }
                      />
                    </LabelledContainer>
                    <LabelledContainer
                      text={t('projectSettings.projection.geoidModel', { ns: 'components' })}
                    >
                      <DropdownSuggestion
                        disabled={isLoadingCoordinateSystems}
                        placeholder={
                          isLoadingCoordinateSystems
                            ? t('loading', { ns: 'common' })
                            : selectedGeoid?.name ?? t('ellipsoidHeight', { ns: 'cloudProcessing' })
                        }
                        portalToBody={true}
                        suggestions={geoids}
                        value={selectedGeoid?.id}
                        width="100%"
                        onClear={() => setSelectedGeoid(undefined)}
                        onSuggestionSelected={(suggestion) => setSelectedGeoid(suggestion)}
                      />
                    </LabelledContainer>
                  </>
                )
              )}
            </Stack>
          </CollapsableSection>
          {showOrthophotoOptions && (
            <CollapsableSection
              expanded={orthotileSectionExpanded}
              title={t('geodata.artifacts.orthoPhoto', { ns: 'common' })}
              onSetExpanded={setOrthotileSectionExpanded}
            >
              <Stack direction="column" spacing={1}>
                <LabelledContainer text={t('geodata.artifacts.orthoPhoto', { ns: 'common' })}>
                  <DropdownSuggestion
                    disabled={!orthoTilesData}
                    placeholder={placeHolderText}
                    portalToBody={true}
                    suggestions={orthoTiles}
                    value={selectedOrthotileIndex}
                    width={'100%'}
                    onClear={() => setSelectedOrthotileIndex(undefined)}
                    onSuggestionSelected={handleOrthotileSelected}
                  />
                </LabelledContainer>
                <LabelledContainer text={t('fileFormat', { ns: 'common' })}>
                  <Select
                    options={[
                      { id: 'GTiff', name: 'TIF' },
                      { id: 'PNG', name: 'PNG' },
                      { id: 'JPEG', name: 'JPEG' },
                      { id: 'WEBP', name: 'WEBP' },
                      { id: 'COG', name: 'COG' },
                    ]}
                    padding={0.5}
                    value={orthoFileFormat}
                    onChange={(e) => setOrthoFileFormat(e.target.value as typeof orthoFileFormat)}
                  />
                </LabelledContainer>
                <LabelledContainer
                  text={t('onDemand.skyviewExport.compression', { ns: 'components' })}
                >
                  <Select
                    options={[
                      { id: 'LZW', name: 'LZW' },
                      { id: 'JPEG', name: 'JPEG' },
                      { id: 'DEFLATE', name: 'DEFLATE' },
                      { id: 'LZ4', name: 'LZ4' },
                      { id: 'ZSTD', name: 'ZSTD' },
                      { id: 'NONE', name: 'NONE' },
                    ]}
                    padding={0.5}
                    value={orthoCompression}
                    onChange={(e) => setOrthoCompression(e.target.value as typeof orthoCompression)}
                  />
                </LabelledContainer>
                <LabelledContainer
                  text={t('onDemand.skyviewExport.maxGsd', {
                    ns: 'components',
                    unit: `${gsdUnit}/px`,
                  })}
                >
                  <TextBox
                    placeholder={t('optional', { ns: 'common' })}
                    type="number"
                    value={orthoMaxGsd}
                    onChange={(e) => setOrthoMaxGsd(e.target.value)}
                  />
                </LabelledContainer>
                <CheckBox
                  checked={orthoCreateWorldFile}
                  onChange={(e) => setOrthoCreateWorldFile(e.target.checked)}
                >
                  {t('onDemand.skyviewExport.createWorldFile', { ns: 'components' })}
                </CheckBox>
              </Stack>
            </CollapsableSection>
          )}
          {showPointCloudOptions && (
            <CollapsableSection
              expanded={pointCloudSectionExpanded}
              title={t('geodata.artifacts.pointCloud', { ns: 'common' })}
              onSetExpanded={setPointCloudSectionExpanded}
            >
              <Stack direction="column" spacing={1}>
                <LabelledContainer text={t('geodata.artifacts.pointCloud', { ns: 'common' })}>
                  <DropdownSuggestion
                    disabled={!pointCloudsData}
                    placeholder={placeHolderText}
                    portalToBody={true}
                    suggestions={pointClouds}
                    value={selectedPointCloudIndex}
                    width={'100%'}
                    onClear={() => setSelectedPointCloudIndex(undefined)}
                    onSuggestionSelected={handlePointCloudSelected}
                  />
                </LabelledContainer>
                <LabelledContainer text={t('fileFormat', { ns: 'common' })}>
                  <Select
                    options={pointCloudFormatOptions}
                    padding={0.5}
                    value={pointCloudFormat}
                    onChange={(e) => setPointCloudFormat(e.target.value as typeof pointCloudFormat)}
                  />
                </LabelledContainer>
                {(pointCloudFormat === 'DTM' || pointCloudFormat === 'DSM') && (
                  <LabelledContainer
                    text={t('onDemand.skyviewExport.rasterResolution', {
                      ns: 'components',
                      unit: `${gsdUnit}/px`,
                    })}
                  >
                    <TextBox
                      type="number"
                      value={pointCloudRasterResolution}
                      onChange={(e) => setPointCloudRasterResolution(e.target.value)}
                    />
                  </LabelledContainer>
                )}
              </Stack>
            </CollapsableSection>
          )}
          {showTilingOptions && (
            <CollapsableSection
              expanded={tilingSectionExpanded}
              title={t('onDemand.skyviewExport.tiles', { ns: 'components' })}
              onSetExpanded={setTilingSectionExpanded}
            >
              <Stack direction="column" spacing={1}>
                {!isDefined(selectedOrthotileIndex) && isDefined(selectedPointCloudIndex) ? (
                  <LabelledContainer
                    text={t('onDemand.skyviewExport.tileSizeMeters', { ns: 'components' })}
                  >
                    <TextBox
                      type="number"
                      value={tilingFallbackBlockSizeMeters}
                      onChange={(e) => setTilingFallbackBlockSizeMeters(e.target.value)}
                    />
                  </LabelledContainer>
                ) : (
                  <LabelledContainer
                    text={t('onDemand.skyviewExport.tileSizePixels', { ns: 'components' })}
                  >
                    <TextBox
                      type="number"
                      value={tilingPixelSize}
                      onChange={(e) => setTilingPixelSize(e.target.value)}
                    />
                  </LabelledContainer>
                )}
              </Stack>
            </CollapsableSection>
          )}
          {showCesium3DTilesOptions && (
            <CollapsableSection
              expanded={true}
              title={t('geodata.artifacts.3dModel', { ns: 'common' })}
              onSetExpanded={() => {
                /*  */
              }}
            >
              <Stack direction="column" spacing={1}>
                <LabelledContainer text={t('geodata.artifacts.3dModel', { ns: 'common' })}>
                  <DropdownSuggestion
                    disabled={!tiledModelsData}
                    placeholder={placeHolderText}
                    portalToBody={true}
                    suggestions={tiledModels}
                    value={selectedTiledModelIndex}
                    width={'100%'}
                    onClear={() => setSelectedTiledModelIndex(undefined)}
                    onSuggestionSelected={handleTiledModelSelected}
                  />
                </LabelledContainer>
                <LabelledContainer text={t('fileFormat', { ns: 'common' })}>
                  <Select
                    options={[
                      { id: '3DTILES', name: '3DTILES' },
                      { id: 'SLPK', name: 'SLPK' },
                    ]}
                    padding={0.5}
                    value={cesium3DTilesFormat}
                    onChange={(e) => setCesium3DTilesFormat(e.target.value as '3DTILES' | 'SLPK')}
                  />
                </LabelledContainer>
                <CheckBox
                  checked={convertToWgs84}
                  onChange={(e) => setConvertToWgs84(e.target.checked)}
                >
                  {t('onDemand.skyviewExport.convertToWgs84', { ns: 'components' })}
                </CheckBox>
              </Stack>
            </CollapsableSection>
          )}
        </CollapsableSectionGroup>
      </RightPanelContent>

      <RightPanelFooter>
        <Stack direction="column" spacing={0.5}>
          {errorList}
          <Button
            color="primary"
            disabled={isExportDisabled || isExportPending}
            loading={isExportPending}
            variant="contained"
            width="100%"
            onClick={handleExport}
          >
            {t('export', { ns: 'common' })}
          </Button>
        </Stack>
      </RightPanelFooter>
    </>
  );
};

export const SkyviewExportMenu = {
  Component,
};
